import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/auth";
import { useJsApiLoader } from "@react-google-maps/api";
import { LoadScript } from "@react-google-maps/api";
import Autocomplete from "@react-google-maps/api";
import { ToastContainer } from "react-toastify";
import OnboardingPage from "../src/OnboardingPage/OnboardingPage";
import OnboardingFlow from "./OnboardingPage/OnboardingFlow";
import SignUp from "../src/OnboardingPage/SignUp";
import ProfilePicturePage from "../src/OnboardingPage/ProfilePicturePage";
import "react-toastify/dist/ReactToastify.css";

import LoginScreen from "../src/OnboardingPage/LoginScreen";
import PasswordRecovery from "../src/OnboardingPage/PasswordRecovery";
import SetNewPassword from "../src/OnboardingPage/SetNewPassword";

import Dashboard from "./home/Home";
import Home from "./home/Home";
import Sidebar from "./components/sidebar/Sidebar";

import EventManagementDetails from "./eventManagementDetails/EventManagementDetails";
import EventManagement from "./eventManagement/EventManagement";
import CreateEvent from "./pages/createEvent/CreateEvent";
import GuestTab from "./pages/guestTab/GuestTab";
import Account from "./pages/account/Account";
import DeactivateAccount from "./components/deactivateAccount/DeactivateAccount";
import Notifications from "./pages/notifications/Notifications";
import HelpDeskSupport from "./pages/helpDeskSupport/HelpDeskSupport";
import Invites from "./pages/invites/Invites";
import "./App.css";
import JoinEvent from "./pages/joinEvent/JoinEvent";
import PrivacyPolicy from "./pages/privacyPolicy/PrivacyPolicy";
import TermsAndConditions from "./pages/termsAndConditions/TermsAndConditions";
import MobileRedirect from "./components/MobileRedirect.js";
import JoinEventRedirect from "./components/joinEventRedirect.js";

import LandingPage from "./landingPage/LandingPage.js";
import InviteCodeModal from "./Modals/inviteCodeModal/InviteCodeModal.js";
import AboutUs from "./pages/aboutUs/AboutUs.js";
import GetInTouch from "./pages/getinTouch/GetInTouch.js";
import FAQDetails from "./pages/faqDetails/FAQDetails.js";

import EditEvent from "../src/pages/editEvent/EditEvent.jsx";

import CheckoutMoniepointPage from "./pages/checkout/CheckoutMoniepointPage";
import CheckoutPaystackPage from "./pages/checkout/CheckoutPaystackPage.jsx";
import StripeCheckoutWrapper from "./pages/checkout/StripeCheckoutWrapper.jsx";
import ProtectedRoute from "./helper/ProtectedRoute";
import InviteLinkHandler from "./components/InviteLinkHandler.jsx";
import DeletedAccountPage from "./components/deletedAccount/DeletedAccount.js";
import StripePaymentResult from "./pages/checkout/StripePaymentResult.jsx";

import ExplorePage from "./pages/explorePage/ExplorePage.jsx";

import ConditionalLayout from "./components/conditionalLayout/ConditionalLayout.jsx";

import StripeMobileReturn from "./pages/checkout/StripeMobileReturn.jsx";

const googleMapsLibraries = ["places"];

const EventRedirect = () => {
  const { eventId } = useParams();
  return <Navigate to={`/eventManagementDetails/${eventId}`} replace />;
};

const GoogleMapsWrapper = ({ children }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY,
    libraries: googleMapsLibraries,
  });

  if (loadError) {
    return <div>Error loading Google Maps. Please try again later.</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  return children;
};

const AuthenticatedLayout = () => (
  <div className="nav-container">
    <div className="desktop-sidebar">
      <Sidebar />
    </div>
    <div className="main-home-content">
      <Outlet />
    </div>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/onboarding" element={<OnboardingFlow />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/password-recovery" element={<PasswordRecovery />} />
          <Route path="/set-new-password" element={<SetNewPassword />} />
          <Route path="/profile-picture" element={<ProfilePicturePage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/get-in-touch" element={<GetInTouch />} />
          <Route path="/faq-details" element={<FAQDetails />} />

          <Route path="/mobile" element={<MobileRedirect />} />
          <Route path="/stripe-return" element={<StripeMobileReturn />} />

          <Route path="/joinEvent/:id" element={<JoinEventRedirect />} />
          <Route path="/joinUsher/:id" element={<JoinEventRedirect />} />

          <Route
            path="/join-Event/:hostId/:code"
            element={<InviteLinkHandler role="guest" />}
          />
          <Route
            path="/join-Usher/:hostId/:code"
            element={<InviteLinkHandler role="usher" />}
          />

          <Route element={<ConditionalLayout />}>
            <Route
              path="/eventManagementDetails/:eventId"
              element={<EventManagementDetails />}
            />
            <Route path="/event/:eventId" element={<EventRedirect />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route element={<AuthenticatedLayout />}>
              <Route path="/home/:role" element={<Home />} />

              <Route path="/eventManagement" element={<EventManagement />} />
              <Route
                path="/createEvent"
                element={
                  <GoogleMapsWrapper>
                    <CreateEvent />
                  </GoogleMapsWrapper>
                }
              />
              <Route
                path="/edit-event/:eventId"
                element={
                  <GoogleMapsWrapper>
                    <EditEvent />
                  </GoogleMapsWrapper>
                }
              />

              <Route path="/guestTab" element={<GuestTab />} />
              <Route path="/invites" element={<Invites />} />
              <Route path="/joinEvent" element={<JoinEvent />} />
              <Route
                path="/join-Event/:hostId/:code"
                element={<JoinEvent role="guest" />}
              />
              <Route
                path="/join-Usher/:hostId/:code"
                element={<JoinEvent role="usher" />}
              />

              {/* <Route
            path="/checkout/monnify"
            element={<CheckoutMoniepointPage />}
          />
          <Route path="/checkout/paystack" element={<CheckoutPaystackPage />} />
          <Route path="/checkout/stripe" element={<CheckoutStripePage />} /> */}

              <Route path="/account" element={<Account />} />
              <Route
                path="/account/deactivate"
                element={<DeactivateAccount />}
              />
              <Route path="/account/deleted" element={<DeletedAccountPage />} />

              <Route path="/notifications" element={<Notifications />} />
              <Route path="/HelpDesk" element={<HelpDeskSupport />} />

              <Route
                path="/checkout/monnify"
                element={<CheckoutMoniepointPage />}
              />
              <Route
                path="/checkout/paystack"
                element={<CheckoutPaystackPage />}
              />
              <Route
                path="/checkout/stripe"
                element={<StripeCheckoutWrapper />}
              />
              <Route path="/payment-result" element={<StripePaymentResult />} />
            </Route>
          </Route>
        </Routes>

        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
